import { Component, Suspense, useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { DoubleSide } from "three";

const mouse = { x: 0, y: 0 };

class GLBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

const FieldRows = () => {
  const rows = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => ({
        x: -14.5 + i * 1.15,
        color: i % 2 === 0 ? "#1d8a52" : "#14663d",
      })),
    []
  );
  return (
    <group>
      {rows.map((r, i) => (
        <mesh key={i} position={[r.x, 0.12, 2.5]}>
          <boxGeometry args={[0.55, 0.24, 15]} />
          <meshStandardMaterial color={r.color} roughness={0.85} />
        </mesh>
      ))}
    </group>
  );
};

const Trees = () => {
  const spots = useMemo(
    () => [
      [-16, -6.5, 1.2],
      [-11.5, -7.2, 1.6],
      [-6.5, -6.6, 1.1],
      [-2, -7.3, 1.5],
      [2.5, -6.7, 1.2],
      [-18.5, -2, 1.4],
    ],
    []
  );
  return (
    <group>
      {spots.map(([x, z, s], i) => (
        <group key={i} position={[x, 0, z]} scale={s}>
          <mesh position={[0, 0.35, 0]}>
            <cylinderGeometry args={[0.09, 0.12, 0.7, 6]} />
            <meshStandardMaterial color="#0b1f16" roughness={1} />
          </mesh>
          <mesh position={[0, 1.15, 0]}>
            <coneGeometry args={[0.62, 1.5, 7]} />
            <meshStandardMaterial color="#14663d" roughness={0.9} />
          </mesh>
        </group>
      ))}
    </group>
  );
};

const Greenhouse = () => (
  <group position={[-8, 0, -2.5]} rotation={[0, 0.25, 0]}>
    <mesh position={[0, 0.1, 0]}>
      <boxGeometry args={[3.4, 0.2, 5.4]} />
      <meshStandardMaterial color="#0d3524" roughness={0.9} />
    </mesh>
    <mesh position={[0, 0.2, 0]} rotation={[Math.PI / 2, 0, Math.PI / 2]}>
      <cylinderGeometry args={[1.7, 1.7, 5.2, 18, 1, true, 0, Math.PI]} />
      <meshStandardMaterial color="#9df0c3" transparent opacity={0.22} side={DoubleSide} roughness={0.2} metalness={0.3} />
    </mesh>
    <mesh position={[0, 0.85, 0]} rotation={[Math.PI / 2, 0, Math.PI / 2]}>
      <cylinderGeometry args={[1.72, 1.72, 5.25, 6, 1, true, 0, Math.PI]} />
      <meshStandardMaterial color="#48e38e" wireframe transparent opacity={0.35} />
    </mesh>
  </group>
);

const CONTAINER_COLORS = ["#48e38e", "#0e3a24", "#2ea866", "#e8f9f0", "#14663d"];

const ContainerYard = () => {
  const boxes = useMemo(() => {
    const arr = [];
    const coords = [
      [9, 0.4, -0.5, 0], [10.6, 0.4, -0.5, 1], [12.2, 0.4, -0.5, 2],
      [9.8, 0.4, -2.2, 3], [11.4, 0.4, -2.2, 4],
      [9.8, 1.2, -0.5, 2], [11.4, 1.2, -2.2, 0],
      [10.6, 1.2, -0.5, 3],
    ];
    coords.forEach(([x, y, z, c], i) => arr.push({ x, y, z, color: CONTAINER_COLORS[c], key: i }));
    return arr;
  }, []);
  return (
    <group>
      {boxes.map((b) => (
        <mesh key={b.key} position={[b.x, b.y, b.z]}>
          <boxGeometry args={[1.5, 0.75, 0.85]} />
          <meshStandardMaterial color={b.color} roughness={0.55} metalness={0.15} />
        </mesh>
      ))}
    </group>
  );
};

const Crane = () => {
  const sway = useRef();
  useFrame(({ clock }) => {
    if (sway.current) sway.current.rotation.z = Math.sin(clock.getElapsedTime() * 0.7) * 0.06;
  });
  return (
    <group position={[8, 0, -4.5]}>
      {[-1.4, 1.4].map((dx) =>
        [-1, 1].map((dz) => (
          <mesh key={`${dx}${dz}`} position={[dx, 2, dz]}>
            <boxGeometry args={[0.28, 4, 0.28]} />
            <meshStandardMaterial color="#0b1f16" roughness={0.7} />
          </mesh>
        ))
      )}
      <mesh position={[0, 4.1, 0]}>
        <boxGeometry args={[0.35, 0.35, 6.5]} />
        <meshStandardMaterial color="#14663d" roughness={0.6} />
      </mesh>
      <group ref={sway} position={[0, 3.95, -2]}>
        <mesh position={[0, -0.9, 0]}>
          <cylinderGeometry args={[0.02, 0.02, 1.8, 4]} />
          <meshStandardMaterial color="#48e38e" />
        </mesh>
        <mesh position={[0, -1.95, 0]}>
          <boxGeometry args={[1.4, 0.7, 0.8]} />
          <meshStandardMaterial color="#48e38e" roughness={0.5} />
        </mesh>
      </group>
    </group>
  );
};

const Ship = () => {
  const ref = useRef();
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    ref.current.position.y = Math.sin(t * 0.6) * 0.1;
    ref.current.rotation.z = Math.sin(t * 0.45) * 0.018;
  });
  return (
    <group ref={ref} position={[13.5, 0.15, -3.2]} rotation={[0, -0.35, 0]}>
      <mesh position={[0, 0.3, 0]}>
        <boxGeometry args={[7, 0.9, 2.2]} />
        <meshStandardMaterial color="#0b1f16" roughness={0.6} />
      </mesh>
      <mesh position={[3.9, 0.35, 0]} rotation={[0, 0, -Math.PI / 2]}>
        <coneGeometry args={[1.15, 1.7, 4]} />
        <meshStandardMaterial color="#0b1f16" roughness={0.6} />
      </mesh>
      <mesh position={[-2.7, 1.35, 0]}>
        <boxGeometry args={[1, 1.6, 1.6]} />
        <meshStandardMaterial color="#e8f9f0" roughness={0.4} />
      </mesh>
      {[[-0.8, 0], [0.6, 0], [2, 0], [-0.1, 0.78], [1.3, 0.78]].map(([dx, dy], i) => (
        <mesh key={i} position={[dx, 1.15 + dy, 0]}>
          <boxGeometry args={[1.3, 0.72, 1.5]} />
          <meshStandardMaterial color={CONTAINER_COLORS[i % CONTAINER_COLORS.length]} roughness={0.55} />
        </mesh>
      ))}
    </group>
  );
};

const JourneyDot = () => {
  const dot = useRef();
  const glow = useRef();
  useFrame(({ clock }) => {
    const t = (clock.getElapsedTime() * 0.07) % 1;
    const x = -13 + t * 27;
    const z = 3 - Math.sin(t * Math.PI) * 6.5;
    const y = 0.7 + Math.sin(t * Math.PI * 4) * 0.18;
    dot.current.position.set(x, y, z);
    glow.current.position.set(x, y + 0.4, z);
  });
  return (
    <>
      <mesh ref={dot}>
        <sphereGeometry args={[0.15, 12, 12]} />
        <meshBasicMaterial color="#48e38e" />
      </mesh>
      <pointLight ref={glow} intensity={7} distance={5.5} color="#48e38e" />
    </>
  );
};

const CameraRig = () => {
  useFrame(({ camera, clock }) => {
    const t = clock.getElapsedTime();
    const tx = mouse.x * 2.6 + Math.sin(t * 0.12) * 0.9;
    const ty = 7.2 + mouse.y * 1.1;
    camera.position.x += (tx - camera.position.x) * 0.045;
    camera.position.y += (ty - camera.position.y) * 0.045;
    camera.lookAt(3, 0.3, -0.5);
  });
  return null;
};

const Scene = () => (
  <>
    <ambientLight intensity={0.55} color="#c9ffe3" />
    <directionalLight position={[-6, 12, 8]} intensity={1.15} color="#eafff3" />
    <pointLight position={[9, 5, -2]} intensity={14} distance={16} color="#48e38e" />

    <mesh position={[-4, -0.26, 0]}>
      <boxGeometry args={[34, 0.5, 26]} />
      <meshStandardMaterial color="#0a2b1b" roughness={0.95} />
    </mesh>

    <FieldRows />
    <Trees />
    <Greenhouse />

    <mesh position={[2.5, 0.03, 4.8]} rotation={[0, -0.32, 0]}>
      <boxGeometry args={[24, 0.07, 1.5]} />
      <meshStandardMaterial color="#04120b" roughness={1} />
    </mesh>

    <mesh position={[10.5, 0.04, -2]}>
      <boxGeometry args={[7.5, 0.32, 9]} />
      <meshStandardMaterial color="#0d3524" roughness={0.85} />
    </mesh>

    <ContainerYard />
    <Crane />

    <mesh position={[19.5, 0.02, -4]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[14, 26]} />
      <meshStandardMaterial color="#07271d" roughness={0.15} metalness={0.55} />
    </mesh>

    <Ship />
    <JourneyDot />
    <CameraRig />
  </>
);

export const HeroScene3D = () => {
  useEffect(() => {
    const onMove = (e) => {
      mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -((e.clientY / window.innerHeight) * 2 - 1);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <GLBoundary>
      <Canvas
        dpr={[1, 1.6]}
        camera={{ position: [0, 7.2, 17.5], fov: 42 }}
        gl={{ antialias: true, alpha: false, powerPreference: "high-performance" }}
        style={{ pointerEvents: "none" }}
      >
        <color attach="background" args={["#04120b"]} />
        <fog attach="fog" args={["#04120b", 15, 44]} />
        <Suspense fallback={null}>
          <Scene />
        </Suspense>
      </Canvas>
    </GLBoundary>
  );
};
